import { DownOutlined, PlusOutlined } from '@ant-design/icons';
import { Badge, Button, Card, Col, Dropdown, Row, Skeleton, Space, Tooltip, Typography, theme } from 'antd';
import { useSession } from 'next-auth/react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { isValidPhoneNumber } from 'react-phone-number-input/max';
import { EditUserFormValues } from '../../../../../../../../types/userform';
import GrispiAutoCompleteInput, {
    AutoCompleteKeyMap
} from '../../../../../../../components/GrispiInput/components/GrispiAutoCompleteInput';
import GrispiInput from '../../../../../../../components/GrispiInput/index';
import ProfilePicture from '../../../../../../../components/ProfilePicture';
import UserRoleSelect from '../../../../../../../components/TicketInfoLeftSideTabs/UserRoleSelect';
import useCompanySettings from '../../../../../../../hooks/adminPage/useCompanySettings';
import useGroups from '../../../../../../../hooks/adminPage/useGroups';
import { useTranslatedGroupOptions } from '../../../../../../../hooks/adminPage/useGroupsAsOptions';
import useUserById, { deriveCustomizedDetailedUser } from '../../../../../../../hooks/useUserById';
import { roles, settingsKey } from '../../../../../../../model/types';
import { sendEmailVerification, verifyUserInfo } from '../../../../../../../services/people/userServices';
import { grispiNotification } from '../../../../../../../store/useNotificationStore';
import { getCurrentUserRole, isAdmin, isAgent, isEndUser } from '../../../../../../../utils/accountHelper';
import { isEmail } from '../../../../../../../utils/forms/formHelper.validators';
import { capitalizeFirstLetter, getUserCredentialsToShow, nullishValue } from '../../../../../../../utils/helpers';
import requestWithMessage from '../../../../../../../utils/requestWithMessage';
import UserInfoVerificationContainer from '../RequesterViewContainer/UserInfoVerificationContainer';

const { Text } = Typography;

const styles = {
    downIcon: { marginTop: 5 },
    downIconFirst: { marginTop: 28 },
    root: { padding: '10px 10px' },
    tooltip: { color: 'inherit' }
};
const cardStyle = { borderRadius: '5px', border: '1px solid rgba(0, 0, 0, 0.12)' };

const InputDropdownActionMap = {
    '1': 'Verify now',
    '2': 'Resend verification email',
    '3': 'Make primary',
    '4': 'Remove'
} as const;
export type userInfoType = 'phone' | 'email';

type actionCallback = (params: {
    emailOrPhone: string;
    infoType?: userInfoType;
    verifyFormText?: string;
}) => (key: keyof typeof InputDropdownActionMap) => Promise<void>;

const onClick =
    (action: (key: keyof typeof InputDropdownActionMap) => Promise<void>) =>
    ({ key }) =>
        action(key);

interface InputDropdown {
    action: actionCallback;
    firstItem: boolean;
    primary?: boolean;
    role: roles;
    type: userInfoType;
    himself?: boolean;
    disabled?: boolean;
}

const InputDropdown = ({ action, role, firstItem, primary = false, type, value, disabled = false }) => {
    const { t } = useTranslation('common');
    const watchedPrimaryEmail = useWatch<EditUserFormValues, 'primaryEmail'>({ name: 'primaryEmail' });
    const isPrimaryEmailAvailable =
        !!watchedPrimaryEmail?.email && !watchedPrimaryEmail?.email?.split('@').includes('example.com');
    const isValidPhone = value?.phone && isValidPhoneNumber(value?.phone);
    const isValidEmail = value?.email && isEmail(value?.email);

    const items = Object.entries(InputDropdownActionMap).map(([key, actionText]) => ({
        disabled:
            (actionText === 'Verify now' && (value?.verified || !isAdmin(getCurrentUserRole()))) || // only admin can verify emails/phones
            (actionText === 'Verify now' && type === 'phone' && !isValidPhone) || // only valid phones can be verified
            (actionText === 'Verify now' && type === 'email' && !isValidEmail) || // only valid emails can be verified
            (actionText === 'Resend verification email' && (value?.verified || type === 'phone')) || // can't resend verification email for phone
            (actionText === 'Resend verification email' && isEndUser(role)) || // FIXME: remove later. end-users will not receive any verification email, currently.
            (actionText === 'Make primary' && primary) || // can't make primary if already primary
            (actionText === 'Make primary' && type === 'phone' && !isValidPhone) || // can't make primary if invalid phone
            (actionText === 'Make primary' && type === 'email' && !isValidEmail) || // can't make primary if invalid email
            (actionText === 'Remove' && primary && type === 'email') || // primary e-mail can't be removed
            (actionText === 'Remove' && primary && type === 'phone' && !isPrimaryEmailAvailable) || // primary phone can't be removed unless primary email is available
            (actionText !== 'Remove' && ((type === 'phone' && !value?.phone) || (type === 'email' && !value?.email))), // if value null, all options will disabled except remove option
        label: t(`multipleEmailPhoneActionDropdown.${key}` as any) as string,
        key,
        onClick: onClick(action)
    }));

    return (
        <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight" disabled={disabled}>
            <Button
                type="link"
                style={firstItem ? styles.downIconFirst : styles.downIcon}
                size="small"
                disabled={disabled}
            >
                <DownOutlined />
            </Button>
        </Dropdown>
    );
};

const StandartUserFieldsContainer = ({ userId, isActive = true }: { userId: string; isActive?: boolean }) => {
    const { t } = useTranslation('forms');
    const { t: tCommon } = useTranslation('common');
    const {
        token: { marginSM, paddingXXS }
    } = theme.useToken();
    const {
        data: {
            userInfo: { userId: currentUserId, rls }
        }
    } = useSession();
    const isCurrentUser = currentUserId?.toString() === userId?.toString();
    const { data, isLoading, mutate } = useUserById(userId);
    const requesterInfo = deriveCustomizedDetailedUser(data);
    const { data: companySettings } = useCompanySettings();
    const { data: groupList, isLoading: groupListIsLoading, error: groupListError } = useGroups();

    const ribbonText = isCurrentUser ? tCommon('you') : !requesterInfo?.enabled ? tCommon('activatedStatus.false') : '';
    const ribbonColor = isCurrentUser
        ? ''
        : isEndUser(requesterInfo?.role?.authority)
        ? 'transparent'
        : !requesterInfo?.enabled
        ? 'red'
        : 'transparent';

    const {
        watch,
        setValue,
        getValues,
        formState: { defaultValues }
    } = useFormContext<EditUserFormValues>();
    const secondaryEmailFields = useWatch<EditUserFormValues, 'secondaryEmails'>({ name: 'secondaryEmails' });
    const secondaryPhoneFields = useWatch<EditUserFormValues, 'secondaryPhones'>({ name: 'secondaryPhones' });
    const disabledGroupID = companySettings?.find((setting) => setting.key === settingsKey.DEFAULT_USER_GROUP)?.value;
    const groupOptions = useTranslatedGroupOptions(
        groupList?.map(({ id, name }) => ({
            value: id.toString(),
            label: name,
            disabled: id?.toString() === disabledGroupID
        }))
    );

    const isUserNotEndUser = requesterInfo?.role?.authority !== 'ROLE_END_USER';
    const isCurrentUserAgent = isAgent(rls[0]);
    const handleGroupsChange = (value: string): string => {
        if (!value) return disabledGroupID;
        if (!value?.split(',').includes(disabledGroupID)) return value?.split(',').concat([disabledGroupID]).join(',');
        return value;
    };
    const actionOnPrimaryInput: actionCallback =
        ({ emailOrPhone, infoType, verifyFormText }) =>
        async (actionKey) => {
            try {
                if (emailOrPhone) {
                    if (actionKey === '1') {
                        await requestWithMessage({
                            key: actionKey,
                            finishMessage: tCommon('message.onVerification', {
                                infoType: capitalizeFirstLetter(tCommon(infoType)),
                                param: emailOrPhone
                            }),
                            apiRequest: async () => {
                                await verifyUserInfo(emailOrPhone, infoType);
                                await mutate();
                                await setValue(verifyFormText as any, true);
                            }
                        });
                        return;
                    }
                    if (actionKey === '2') {
                        await requestWithMessage({
                            key: actionKey,
                            finishMessage: tCommon('message.onVerificationMail' as any),
                            apiRequest: () => sendEmailVerification(emailOrPhone)
                        });
                        return;
                    }
                    if (actionKey === '4' && infoType === 'phone') {
                        if (secondaryPhoneFields.length > 0) {
                            grispiNotification('warning', 'You should remove other phone numbers first!');
                            return;
                        }

                        return setValue(
                            'primaryPhone',
                            { phone: null, verified: false },
                            {
                                shouldValidate: true,
                                shouldDirty: true,
                                shouldTouch: true
                            }
                        );
                    }
                }
            } catch (_error) {
                // console.error('EditableRequesterInfo actionOnPrimaryInput error:', error);
            }
        };

    return (
        <Space direction="vertical" size={10} className="full-width" style={{ marginTop: marginSM }}>
            <Badge.Ribbon text={ribbonText} color={ribbonColor}>
                <Card bodyStyle={{ padding: paddingXXS * 3 }} style={cardStyle}>
                    <Row>
                        <Col>
                            <ProfilePicture src={data?.profilePicture} />
                        </Col>
                        <Col offset={1} flex="auto">
                            <Space className="full-width" direction="vertical" size="small">
                                <Text strong>{getUserCredentialsToShow(data, true)}</Text>
                                <Controller<EditUserFormValues, 'roleToChange'>
                                    name="roleToChange"
                                    render={({ field: { value, onChange } }) =>
                                        isLoading ? (
                                            <Skeleton.Input active className="full-width" />
                                        ) : (
                                            <UserRoleSelect
                                                onChange={onChange}
                                                role={value}
                                                userInfo={requesterInfo}
                                                disabled={!isActive}
                                            />
                                        )
                                    }
                                />
                            </Space>
                        </Col>
                    </Row>
                </Card>
            </Badge.Ribbon>
            <Space size={10} className="full-width" direction="vertical">
                <Card bodyStyle={{ padding: paddingXXS * 3 }} style={cardStyle}>
                    <Space direction="vertical" className="full-width" size="small">
                        <Controller<EditUserFormValues, 'fullName'>
                            key="fullName"
                            name="fullName"
                            render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                                return (
                                    <>
                                        <GrispiInput
                                            containerProps={{
                                                errorMessage: error?.message,
                                                label: t('form.fullName.label')
                                            }}
                                            type="TEXT"
                                            onChange={onChange}
                                            onBlur={onBlur}
                                            value={value}
                                            antdProps={{ placeholder: 'John Doe' }}
                                            disabled={!isActive}
                                        />
                                    </>
                                );
                            }}
                            rules={{
                                validate: (value) => {
                                    if (!value) return true;
                                    const finishEmptySpacePattern = /^.*(\S)$/;
                                    if (!finishEmptySpacePattern.test(value))
                                        return t('form.fullName.cannotFinishWithEmptySpace');
                                    const cannotStartWithEmptySpace = /^(\S).*$/;
                                    if (!cannotStartWithEmptySpace.test(value))
                                        return t('form.fullName.cannotStartWithEmptySpace');
                                    const cannotStartWithDash = /^(?!-).*$/;
                                    if (!cannotStartWithDash.test(value)) return t('form.fullName.cannotStartWithDash');
                                    const cannotHaveCharacters = /^(\p{L}|\s|-)*$/gu;
                                    if (!cannotHaveCharacters.test(value))
                                        return t('form.fullName.cannotHaveCharacters');
                                    return true;
                                },
                                required: t('form.fullName.required')
                            }}
                        />
                        <Controller<EditUserFormValues, 'organization'>
                            key="organization"
                            name="organization"
                            render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                                return (
                                    <GrispiAutoCompleteInput
                                        autocompleteKey={AutoCompleteKeyMap.organization}
                                        containerProps={{
                                            errorMessage: error?.message,
                                            label: t('form.organization.label')
                                        }}
                                        onChange={onChange}
                                        onBlur={onBlur}
                                        value={value}
                                        disabled={!isActive}
                                    />
                                );
                            }}
                        />
                        {isUserNotEndUser && (
                            <Controller<EditUserFormValues, 'groups'>
                                key="groups"
                                name="groups"
                                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                                    return (
                                        <Tooltip title={groupListError?.message}>
                                            {groupListIsLoading ? (
                                                <Space direction="vertical" className="full-width">
                                                    <Skeleton.Input active size="small" />
                                                    <Skeleton.Input active className="full-width" />
                                                </Space>
                                            ) : (
                                                <GrispiInput
                                                    containerProps={{
                                                        errorMessage: error?.message,
                                                        label: t('form.groups.label'),
                                                        actionButton: (
                                                            <Button
                                                                type="link"
                                                                size="small"
                                                                style={{ fontSize: 11, paddingRight: 0 }}
                                                                onClick={() => {
                                                                    setValue(
                                                                        'groups',
                                                                        groupOptions
                                                                            .map(({ value }) => value)
                                                                            ?.join(','),
                                                                        {
                                                                            shouldValidate: true,
                                                                            shouldDirty: true
                                                                        }
                                                                    );
                                                                }}
                                                                disabled={!isActive || isCurrentUserAgent}
                                                            >
                                                                <p> {t('form.groups.rightActionText')}</p>
                                                            </Button>
                                                        )
                                                    }}
                                                    type="MULTI_SELECT"
                                                    onChange={(v) => {
                                                        onChange(handleGroupsChange(v));
                                                    }}
                                                    onBlur={onBlur}
                                                    value={value}
                                                    antdProps={{
                                                        placeholder: t('form.groups.label')
                                                    }}
                                                    options={groupOptions}
                                                    disabled={!!groupListError || !isActive || isCurrentUserAgent}
                                                />
                                            )}
                                        </Tooltip>
                                    );
                                }}
                            />
                        )}
                    </Space>
                </Card>
                <Card bodyStyle={{ padding: paddingXXS * 3 }} style={cardStyle}>
                    <Space direction="vertical" className="full-width" size="small">
                        <UserInfoVerificationContainer
                            firstItem
                            tooltipProps={{
                                title: (
                                    <Space direction="vertical">
                                        <Text strong style={styles.tooltip}>
                                            {t('form.email.unverified.title')}
                                        </Text>

                                        <Text style={styles.tooltip}> {t('form.email.unverified.description')}</Text>
                                    </Space>
                                ),
                                placement: 'right'
                            }}
                            verified={watch('primaryEmail.verified')}
                        >
                            <Controller<EditUserFormValues, 'primaryEmail.email'>
                                key="primaryEmail.email"
                                name="primaryEmail.email"
                                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                                    return (
                                        <GrispiInput
                                            containerProps={{
                                                errorMessage: error?.message,
                                                label: t('form.email.primaryLabel')
                                            }}
                                            type="TEXT"
                                            onChange={(v) => {
                                                if (defaultValues.primaryEmail?.email !== v) {
                                                    const eMailExists = defaultValues.secondaryEmails.find(
                                                        (email) => email?.email === v
                                                    );
                                                    if (eMailExists)
                                                        setValue('primaryEmail.verified', eMailExists.verified);
                                                    else setValue('primaryEmail.verified', false);
                                                } else
                                                    setValue(
                                                        'primaryEmail.verified',
                                                        defaultValues.primaryEmail?.verified
                                                    );
                                                onChange(v);
                                            }}
                                            onBlur={onBlur}
                                            value={value}
                                            antdProps={{ placeholder: 'email@example.com' }}
                                            disabled={!isActive}
                                        />
                                    );
                                }}
                                rules={{
                                    validate: (value) => {
                                        if (isUserNotEndUser) {
                                            return isEmail(value) || t('form.email.invalid');
                                        } else {
                                            if (watch('primaryEmail.email')) {
                                                if (!value) return true;
                                                if (value?.length === 0) return true;
                                                return isEmail(value) || t('form.email.invalid');
                                            } else {
                                                if (!value) return true;
                                                if (value?.length === 0) return true;
                                                return isEmail(value) || t('form.email.invalid');
                                            }
                                        }
                                    }
                                }}
                            />
                            <InputDropdown
                                action={actionOnPrimaryInput({
                                    emailOrPhone: getValues('primaryEmail')?.email,
                                    infoType: 'email',
                                    verifyFormText: 'primaryEmail.verified'
                                })}
                                firstItem
                                primary
                                type="email"
                                role={requesterInfo?.role?.authority}
                                value={getValues('primaryEmail')}
                                disabled={!isActive}
                            />
                        </UserInfoVerificationContainer>

                        {secondaryEmailFields?.map((secondaryEmail, index) => {
                            const action: actionCallback =
                                ({ emailOrPhone, infoType = 'email', verifyFormText }) =>
                                async (key) => {
                                    if (key === '1' || key === '2')
                                        return actionOnPrimaryInput({ emailOrPhone, infoType, verifyFormText })(key);
                                    const emailToDelete = watch(`secondaryEmails.${index}`);
                                    if (key === '3') {
                                        const primaryEmail = watch(`primaryEmail`);
                                        const newValue = [
                                            ...secondaryEmailFields.filter(
                                                (mail) => mail.email !== emailToDelete.email
                                            ),
                                            primaryEmail
                                        ];
                                        setValue('secondaryEmails', newValue, {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                            shouldTouch: true
                                        });

                                        setValue('primaryEmail', emailToDelete, {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                            shouldTouch: true
                                        });
                                    }
                                    if (key === '4')
                                        return setValue(
                                            'secondaryEmails',
                                            secondaryEmailFields?.filter((mail) => mail.email !== emailToDelete.email),
                                            {
                                                shouldValidate: true,
                                                shouldDirty: true,
                                                shouldTouch: true
                                            }
                                        );
                                };
                            return (
                                <UserInfoVerificationContainer
                                    key={`secondaryEmails.${index}`}
                                    firstItem={index === 0} //nolabel yerine firstitem ı dene
                                    tooltipProps={{
                                        title: (
                                            <Space direction="vertical">
                                                <Text strong style={styles.tooltip}>
                                                    {t('form.email.unverified.title')}
                                                </Text>

                                                <Text style={styles.tooltip}>
                                                    {t('form.email.unverified.description')}
                                                </Text>
                                            </Space>
                                        ),
                                        placement: 'right'
                                    }}
                                    verified={secondaryEmail?.verified}
                                >
                                    <Controller<EditUserFormValues, `secondaryEmails.${number}.email`>
                                        key={`secondaryEmails.${index}.email`}
                                        name={`secondaryEmails.${index}.email`}
                                        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                                            return (
                                                <GrispiInput
                                                    containerProps={{
                                                        errorMessage: error?.message,
                                                        ...{
                                                            ...(index !== 0 && {
                                                                noLabel: true
                                                            })
                                                        },
                                                        label: t('form.email.label')
                                                    }}
                                                    type="TEXT"
                                                    onChange={(v) => {
                                                        if (defaultValues.secondaryEmails[index]?.email !== v) {
                                                            const emailExists = defaultValues.secondaryEmails.find(
                                                                (email) => email?.email === v
                                                            );
                                                            if (emailExists)
                                                                setValue(
                                                                    `secondaryEmails.${index}.verified`,
                                                                    emailExists.verified
                                                                );
                                                            else if (defaultValues.primaryEmail?.email === v)
                                                                setValue(
                                                                    `secondaryEmails.${index}.verified`,
                                                                    defaultValues.primaryEmail?.verified
                                                                );
                                                            else setValue(`secondaryEmails.${index}.verified`, false);
                                                        } else
                                                            setValue(
                                                                `secondaryEmails.${index}.verified`,
                                                                defaultValues.secondaryEmails[index]?.verified
                                                            );
                                                        onChange(v);
                                                    }}
                                                    onBlur={onBlur}
                                                    value={value}
                                                    antdProps={{ placeholder: 'email@example.com' }}
                                                    disabled={!isActive}
                                                />
                                            );
                                        }}
                                        rules={{
                                            validate: (value) => isEmail(value) || t('form.email.invalid'),
                                            required: t('form.email.required')
                                        }}
                                    />
                                    <InputDropdown
                                        action={action({
                                            emailOrPhone: secondaryEmail?.email,
                                            infoType: 'email',
                                            verifyFormText: `secondaryEmails.${index}.verified`
                                        })}
                                        firstItem={index === 0}
                                        type="email"
                                        role={requesterInfo?.role?.authority}
                                        value={getValues(`secondaryEmails.${index}`)}
                                        disabled={!isActive}
                                    />
                                </UserInfoVerificationContainer>
                            );
                        })}
                        {isActive && (
                            <Button
                                block
                                type="link"
                                onClick={() => {
                                    const hasEmptyValue =
                                        secondaryEmailFields.length > 0 &&
                                        secondaryEmailFields.findIndex((email) => nullishValue(email.email)) >= 0;

                                    if (hasEmptyValue) return;
                                    setValue(
                                        'secondaryEmails',
                                        [...secondaryEmailFields, { email: null, verified: false }],
                                        {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                            shouldTouch: true
                                        }
                                    );
                                }}
                                icon={<PlusOutlined />}
                                size="small"
                            >
                                {t('form.email.add')}
                            </Button>
                        )}
                    </Space>
                </Card>
                <Card bodyStyle={{ padding: paddingXXS * 3 }} style={cardStyle}>
                    <Space direction="vertical" className="full-width" size="small">
                        <UserInfoVerificationContainer
                            firstItem
                            tooltipProps={{
                                title: (
                                    <Space direction="vertical">
                                        <Text strong style={styles.tooltip}>
                                            {t('form.phone.unverified.title')}
                                        </Text>

                                        <Text style={styles.tooltip}> {t('form.phone.unverified.description')}</Text>
                                    </Space>
                                ),
                                placement: 'right'
                            }}
                            verified={watch('primaryPhone')?.verified}
                        >
                            <Controller<EditUserFormValues, 'primaryPhone.phone'>
                                key="primaryPhone.phone"
                                name="primaryPhone.phone"
                                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                                    return (
                                        <GrispiInput
                                            containerProps={{
                                                errorMessage: error?.message,
                                                label: t('form.phone.primaryLabel')
                                            }}
                                            type="PHONE"
                                            onChange={(v) => {
                                                if (defaultValues.primaryPhone?.phone !== v) {
                                                    const phoneExists = defaultValues.secondaryPhones.find(
                                                        (phone) => phone?.phone === v
                                                    );
                                                    if (phoneExists)
                                                        setValue('primaryPhone.verified', phoneExists.verified);
                                                    else setValue('primaryPhone.verified', false);
                                                } else
                                                    setValue(
                                                        'primaryPhone.verified',
                                                        defaultValues.primaryPhone?.verified
                                                    );
                                                onChange(v);
                                            }}
                                            onBlur={onBlur}
                                            value={value}
                                            disabled={!isActive}
                                        />
                                    );
                                }}
                                rules={{
                                    validate: (value) => {
                                        if (isUserNotEndUser) {
                                            if (!value) return true;
                                            if (value?.length === 0) return true;
                                            return isValidPhoneNumber(value) || t('form.phone.invalid');
                                        } else {
                                            if (watch('primaryEmail.email')) {
                                                if (!value) return true;
                                                if (value?.length === 0) return true;
                                                return isValidPhoneNumber(value) || t('form.phone.invalid');
                                            } else {
                                                if (!value) return t('form.phone.required');
                                                if (value?.length === 0) return t('form.phone.required');
                                                return isValidPhoneNumber(value) || t('form.phone.invalid');
                                            }
                                        }
                                    }
                                }}
                            />
                            <InputDropdown
                                action={actionOnPrimaryInput({
                                    emailOrPhone: getValues('primaryPhone')?.phone,
                                    infoType: 'phone',
                                    verifyFormText: 'primaryPhone.verified'
                                })}
                                firstItem
                                primary
                                type="phone"
                                role={requesterInfo?.role?.authority}
                                value={getValues('primaryPhone')}
                                disabled={!isActive}
                            />
                        </UserInfoVerificationContainer>
                        {secondaryPhoneFields?.map((secondaryPhone, index) => {
                            const action: actionCallback =
                                ({ emailOrPhone, infoType = 'phone', verifyFormText }) =>
                                async (key) => {
                                    if (key === '1' || key === '2')
                                        return actionOnPrimaryInput({ emailOrPhone, infoType, verifyFormText })(key);

                                    const phoneToDelete = watch(`secondaryPhones.${index}`);

                                    if (key === '3') {
                                        const primaryPhone = watch(`primaryPhone`);
                                        const newValue = [
                                            ...secondaryPhoneFields.filter(
                                                (phone) => phone.phone !== phoneToDelete.phone
                                            ),
                                            primaryPhone
                                        ];

                                        setValue('secondaryPhones', newValue, {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                            shouldTouch: true
                                        });

                                        setValue('primaryPhone', phoneToDelete, {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                            shouldTouch: true
                                        });
                                        return;
                                    }

                                    if (key === '4')
                                        return setValue(
                                            'secondaryPhones',
                                            secondaryPhoneFields?.filter(
                                                (phone) => phone.phone !== phoneToDelete.phone
                                            ),
                                            {
                                                shouldValidate: true,
                                                shouldDirty: true,
                                                shouldTouch: true
                                            }
                                        );
                                };
                            return (
                                <UserInfoVerificationContainer
                                    key={`secondaryPhones.${index}`}
                                    firstItem={index === 0}
                                    tooltipProps={{
                                        title: (
                                            <Space direction="vertical">
                                                <Text strong style={styles.tooltip}>
                                                    {t('form.phone.unverified.title')}
                                                </Text>

                                                <Text style={styles.tooltip}>
                                                    {t('form.phone.unverified.description')}
                                                </Text>
                                            </Space>
                                        ),
                                        placement: 'right'
                                    }}
                                    verified={secondaryPhone?.verified}
                                >
                                    <Controller<EditUserFormValues, `secondaryPhones.${number}.phone`>
                                        key={`secondaryPhones.${index}.phone`}
                                        name={`secondaryPhones.${index}.phone`}
                                        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                                            return (
                                                <GrispiInput
                                                    containerProps={{
                                                        errorMessage: error?.message,
                                                        ...{
                                                            ...(index !== 0 && {
                                                                noLabel: true
                                                            })
                                                        },
                                                        label: t('form.phone.label')
                                                    }}
                                                    type="PHONE"
                                                    onChange={(v) => {
                                                        if (defaultValues.secondaryPhones[index]?.phone !== v) {
                                                            const phoneExists = defaultValues.secondaryPhones.find(
                                                                (phone) => phone?.phone === v
                                                            );
                                                            if (phoneExists)
                                                                setValue(
                                                                    `secondaryPhones.${index}.verified`,
                                                                    phoneExists.verified
                                                                );
                                                            else if (defaultValues.primaryPhone?.phone === v)
                                                                setValue(
                                                                    `secondaryPhones.${index}.verified`,
                                                                    defaultValues.primaryPhone?.verified
                                                                );
                                                            else setValue(`secondaryPhones.${index}.verified`, false);
                                                        } else
                                                            setValue(
                                                                `secondaryPhones.${index}.verified`,
                                                                defaultValues.secondaryPhones[index]?.verified
                                                            );
                                                        onChange(v);
                                                    }}
                                                    onBlur={onBlur}
                                                    value={value}
                                                    disabled={!isActive}
                                                />
                                            );
                                        }}
                                        rules={{
                                            validate: (value) => {
                                                if (!value) return t('form.phone.invalid');
                                                return isValidPhoneNumber(value) || t('form.phone.invalid');
                                            }
                                        }}
                                    />
                                    <InputDropdown
                                        action={action({
                                            emailOrPhone: secondaryPhone.phone,
                                            infoType: 'phone',
                                            verifyFormText: `secondaryPhones.${index}.verified`
                                        })}
                                        firstItem={index === 0}
                                        type="phone"
                                        role={requesterInfo?.role?.authority}
                                        value={getValues(`secondaryPhones.${index}`)}
                                        disabled={!isActive}
                                    />
                                </UserInfoVerificationContainer>
                            );
                        })}
                        {isActive && (
                            <Button
                                block
                                type="link"
                                onClick={() => {
                                    const hasEmptyValue =
                                        secondaryPhoneFields.length > 0 &&
                                        secondaryPhoneFields.findIndex((phone) => nullishValue(phone.phone)) >= 0;

                                    if (hasEmptyValue) return;
                                    setValue(
                                        'secondaryPhones',
                                        [...secondaryPhoneFields, { phone: null, verified: false }],
                                        {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                            shouldTouch: true
                                        }
                                    );
                                }}
                                icon={<PlusOutlined />}
                                size="small"
                            >
                                {t('form.phone.add')}
                            </Button>
                        )}
                    </Space>
                </Card>
            </Space>
        </Space>
    );
};

export default StandartUserFieldsContainer;
